import * as THREE from 'three';
import { forInstancing } from '../materials/materials';

// All cordage is drawn as instances of one unit cylinder: static standing
// rigging first, then dynamic running rigging rewritten every frame. The rope
// material widens sub-pixel ropes and fades them with alpha-to-coverage.

const UP = new THREE.Vector3(0, 1, 0);
const tmpDir = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const tmpS = new THREE.Vector3();
const tmpM = new THREE.Matrix4();

export class RopeSet {
  readonly mesh: THREE.InstancedMesh;
  private staticCount = 0;
  private cursor = 0;
  private locked = false;
  private fullUpload = true;

  constructor(material: THREE.Material, capacity = 5000) {
    const g = new THREE.CylinderGeometry(1, 1, 1, 7, 1, true);
    g.translate(0, 0.5, 0);
    this.mesh = new THREE.InstancedMesh(g, forInstancing(material), capacity);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  }

  private write(i: number, a: THREE.Vector3, b: THREE.Vector3, r: number): void {
    tmpDir.subVectors(b, a);
    const len = tmpDir.length();
    if (len < 1e-5) {
      tmpM.makeScale(0, 0, 0);
    } else {
      tmpQ.setFromUnitVectors(UP, tmpDir.multiplyScalar(1 / len));
      tmpM.compose(a, tmpQ, tmpS.set(r, len, r));
    }
    this.mesh.setMatrixAt(i, tmpM);
  }

  /** Straight segment (static until lock(), dynamic after). */
  add(a: THREE.Vector3, b: THREE.Vector3, r: number): void {
    if (this.cursor >= this.mesh.instanceMatrix.count) return;
    this.write(this.cursor++, a, b, r);
  }

  /** Rope through points (a polyline). */
  polyline(pts: THREE.Vector3[], r: number): void {
    for (let i = 0; i < pts.length - 1; i++) this.add(pts[i]!, pts[i + 1]!, r);
  }

  /** A slack rope between a and b, sagging by `sag` metres. */
  sagging(a: THREE.Vector3, b: THREE.Vector3, r: number, sag: number, segments = 6): void {
    let prev = a;
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const p = new THREE.Vector3().lerpVectors(a, b, t);
      p.y -= Math.sin(t * Math.PI) * sag;
      this.add(prev, p, r);
      prev = p;
    }
  }

  /** Start the static rigging over (after damage). */
  reset(): void {
    this.cursor = 0;
    this.staticCount = 0;
    this.locked = false;
    this.mesh.instanceMatrix.clearUpdateRanges();
  }

  /** End of static rigging: everything after is rebuilt each frame. */
  lock(): void {
    this.staticCount = this.cursor;
    this.locked = true;
    this.fullUpload = true;
    this.mesh.count = this.cursor;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  beginFrame(): void {
    this.cursor = this.staticCount;
  }

  endFrame(): void {
    this.mesh.count = this.cursor;
    this.mesh.instanceMatrix.clearUpdateRanges();
    if (this.fullUpload) this.fullUpload = false;
    else if (this.locked) {
      this.mesh.instanceMatrix.addUpdateRange(this.staticCount * 16, (this.cursor - this.staticCount) * 16);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

/** Instanced small parts (blocks, deadeyes) with per-frame placement. */
export class PartSet {
  readonly mesh: THREE.InstancedMesh;
  private cursor = 0;
  private staticCount = 0;

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, capacity: number) {
    this.mesh = new THREE.InstancedMesh(geometry, forInstancing(material), capacity);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.count = 0;
  }

  add(pos: THREE.Vector3, q: THREE.Quaternion, s = 1): void {
    if (this.cursor >= this.mesh.instanceMatrix.count) return;
    tmpM.compose(pos, q, tmpS.set(s, s, s));
    this.mesh.setMatrixAt(this.cursor++, tmpM);
  }

  /** Oriented with local +Y along dir. */
  addAlong(pos: THREE.Vector3, dir: THREE.Vector3, s = 1): void {
    tmpQ.setFromUnitVectors(UP, tmpDir.copy(dir).normalize());
    this.add(pos, tmpQ, s);
  }

  lock(): void {
    this.staticCount = this.cursor;
    this.mesh.count = this.cursor;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  reset(): void {
    this.cursor = 0;
    this.staticCount = 0;
  }

  beginFrame(): void {
    this.cursor = this.staticCount;
  }

  endFrame(): void {
    this.mesh.count = this.cursor;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
